import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useSearchParams } from 'react-router';

import { FormatEnum, UserControllerListUserEventsParams } from '@/api/services/api/generated/data-contracts';
import { userController } from '@/api/services/api/modules/users';

import useDebounce from './use-debounce';
import { useSearchFilters } from './use-search-filters';

export const userKeys = {
  all: ['users'] as const,
  lists: () => [...userKeys.all, 'lists'] as const,
  list: ({ page, ...filters }: UserControllerListUserEventsParams) => [...userKeys.lists(), page, filters] as const,

  me: ['me'] as const,
};

const ITEMS_PER_PAGE = 6;

export const useUserEvents = () => {
  const { params } = useUserEventsFilters();
  const query = useQuery({
    queryFn: () => userController.listUserEvents({ params: { ...params, role: 'organizer' } }),
    queryKey: userKeys.list(params),
    select: ({ data }) => {
      return data;
    },
  });

  return { ...query, params };
};

export const useUserEventsTotal = () => {
  const { params } = useUserEventsFilters();
  const { setFilter } = useSearchFilters();

  const query = useQuery({
    queryFn: () => userController.listUserEvents({ params: { ...params, page: 1 } }),
    queryKey: userKeys.list({ ...params, page: 1 }),
    select: ({ data }) => data.total,
  });

  // Se a página atual for maior que a quantidade de páginas, mudar para página 1
  useEffect(() => {
    if (query.data === undefined) return;
    const currentPage = params.page || 1;
    const totalPages = Math.ceil(query.data / ITEMS_PER_PAGE);
    if (currentPage !== 1 && (currentPage > totalPages || totalPages === 0)) {
      setFilter({ page: '1' });
    }
  }, [params.page, query.data]);

  return query.data;
};

function useUserEventsFilters() {
  const [searchParams] = useSearchParams();
  const page = Number(searchParams.get('page') ?? 1);
  const status = searchParams.getAll('status').toString();
  const format = searchParams.get('format') as FormatEnum;
  const search = searchParams.get('search') ?? '';
  const debouncedSearch = useDebounce(search, 500);
  const params: UserControllerListUserEventsParams = {
    format,
    role: 'organizer',
    status,
    page,
    perPage: ITEMS_PER_PAGE,
    search: debouncedSearch,
  };
  return { params };
}

export const useMe = () => {
  const query = useQuery({
    queryFn: () => userController.me(),
    queryKey: userKeys.me,
    select: ({ data }) => data,
  });

  return query;
};
